import { Runtime } from '@yada/runtime';
import type { SyncState, AppLocaleContext } from '@yada/types';
import { migrateImplicitNotificationPreferences } from '@yada/types';
import { createHybridStorage } from '@yada/storage';
import { getCurrentWorkspaceId, getAuthenticatedWorkspaces } from '../workspace/workspaceContext';
import { createWorkspace, addMember, listWorkspaces, listMembers } from '@yada/api-client';
import { shareSeedWithProfileDevices } from '@yada/sync';
import { hasHouseholdProfile, getHouseholdProfile } from '../accounts/useHouseholdProfile';
import { setActiveProfileId } from '../accounts/accounts';
import { getGlobalSyncState, onSyncStateChange, flushPendingSync } from '@yada/sync';
import { NotificationManager } from '../notifications/NotificationManager';
import { LlmManager } from '../llm/LlmManager';
import { collectPassiveAppContext } from '../llm/passiveContext';
import { exportFullBackup, importFullBackup } from '../data/DataDumpService';
import {
  getCurrentCountryContext,
  getCurrentLocale,
  COUNTRY_CHANGE_EVENT,
  LOCALE_CHANGE_EVENT,
} from '@yada/i18n';
import { getWeatherService } from '@yada/weather';
import { isCrossAppAccessAllowed } from '../permissions/crossAppPermissions';
import { createServiceApi } from './serviceApi';
import { onProfileChange } from './profileEvents';

function globalSyncStateToAppSyncState(g: ReturnType<typeof getGlobalSyncState>): SyncState {
  if (g.error) return { status: 'error', reason: g.error.reason, since: g.error.since };
  if (g.hasSyncing) return { status: 'syncing' };
  if (g.hasPending) return { status: 'pending', pendingCount: 1 };
  return { status: 'idle' };
}

function buildLocaleContext(onLocaleChangeSubscriber?: (cb: () => void) => () => void): AppLocaleContext {
  const countryCtx = getCurrentCountryContext();
  const language = getCurrentLocale();

  return {
    language,
    country: {
      code: countryCtx.country.code,
      englishName: countryCtx.country.englishName,
      flag: countryCtx.country.flag,
      currency: countryCtx.country.currency,
      currencySymbol: countryCtx.country.currencySymbol,
      dateFormat: countryCtx.country.dateFormat,
      decimalSeparator: countryCtx.country.decimalSeparator,
    },
    countryFeatures: { ...countryCtx.features },
    onChange(cb) {
      if (onLocaleChangeSubscriber) {
        return onLocaleChangeSubscriber(() => cb(buildLocaleContext(onLocaleChangeSubscriber)));
      }
      return () => {};
    },
  };
}

export type ShellRuntime = Runtime & {
  notificationManager: NotificationManager;
  llmManager: LlmManager;
};

export function createShellRuntime(): ShellRuntime {
  const notificationManager = new NotificationManager();
  migrateImplicitNotificationPreferences(notificationManager.permission);
  const llmManager = new LlmManager();

  const runtime: Runtime = new Runtime({
    storage: {
      createStorage: (appId, metadataExtractor) => createHybridStorage(appId, metadataExtractor),
      createWorkspaceStorage: (appId, workspaceId) => createHybridStorage(appId, workspaceId),
    },
    sync: {
      getSyncApi: () => ({
        get state() { return globalSyncStateToAppSyncState(getGlobalSyncState()); },
        onChange(cb) { return onSyncStateChange(g => cb(globalSyncStateToAppSyncState(g))); },
        flush: () => flushPendingSync(),
      }),
    },
    notifications: {
      createApi: (appId) => notificationManager.createApi(appId),
    },
    permissions: {
      isCrossAppAllowed: (caller, target) => isCrossAppAccessAllowed(caller, target),
    },
    api: {
      createApi: () => createServiceApi(),
    },
    llm: {
      createApi: (appId, appName) => llmManager.createApi(appId, appName),
      invokeAgent: async (appId, appName, request, onAccept) => {
        if (onAccept) {
          await llmManager.invokeAgent(appId, appName, request, onAccept);
        }
      },
    },
    workspace: {
      isWorkspaceActive: () => {
        const currentWorkspaceId = getCurrentWorkspaceId();
        const authenticatedWorkspaces = getAuthenticatedWorkspaces();
        return (
          !!currentWorkspaceId &&
          authenticatedWorkspaces.some(w => w.workspaceId === currentWorkspaceId)
        );
      },
      getCurrentWorkspaceId: () => getCurrentWorkspaceId(),
      createWorkspaceApi: (_appId) => {
        const profileId = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:activeProfileId') : null;
        if (!profileId) return undefined;

        return {
          async getOrCreateSharedWith(targetProfileId: string): Promise<{ workspaceId: string }> {
            const cacheKey = `yada:shared-workspace:${profileId}:${targetProfileId}`;
            const cached = localStorage.getItem(cacheKey);
            if (cached) return { workspaceId: cached };

            let foundId: string | undefined;
            try {
              const workspaces = await listWorkspaces();
              const candidates = workspaces.filter(
                w => w.workspace_type === 'group' &&
                  w.membership_status === 'active' &&
                  w.member_count === 2
              );
              for (const candidate of candidates) {
                const members = await listMembers(candidate.id);
                if (members.some(m => m.profile_id === targetProfileId)) {
                  foundId = candidate.id;
                  break;
                }
              }
            } catch {}

            if (!foundId) {
              const storageUrl = localStorage.getItem('yada:defaultStorageUrl') ?? '';
              const ws = await createWorkspace({
                name: `shared:${targetProfileId}`,
                storage_url: storageUrl,
                workspace_type: 'group',
              });
              foundId = ws.id;

              try {
                await addMember(foundId, targetProfileId, 'member');
              } catch (err) {
                console.warn('[workspace] addMember failed:', err);
              }

              try {
                await shareSeedWithProfileDevices(foundId, targetProfileId);
              } catch (err) {
                console.warn('[workspace] shareSeedWithProfileDevices failed:', err);
              }
            }

            localStorage.setItem(cacheKey, foundId);
            return { workspaceId: foundId };
          },

          getStorageForWorkspace(wsAppId: string, wsId: string) {
            return createHybridStorage(wsAppId, wsId);
          },

          async getSharedWorkspaces(): Promise<string[]> {
            try {
              const workspaces = await listWorkspaces();
              return workspaces
                .filter(w => w.workspace_type !== 'personal' && w.membership_status === 'active')
                .map(w => w.id);
            } catch {
              return [];
            }
          },

          async getHouseholdWorkspace(): Promise<{ workspaceId: string } | null> {
            try {
              if (!(await hasHouseholdProfile())) return null;
              const workspaces = await listWorkspaces();
              const household = workspaces.find(
                w => w.workspace_type === 'group' && w.membership_status === 'active'
              );
              return household ? { workspaceId: household.id } : null;
            } catch {
              return null;
            }
          },
        };
      },
    },
    locale: {
      getLocaleContext: (): AppLocaleContext => buildLocaleContext((cb) => runtime.on('locale:change', cb)),
    },
    weather: {
      getWeatherService: () => getWeatherService(),
    },
    backup: {
      exportFullBackup: () => exportFullBackup(),
      importFullBackup: (data) => importFullBackup(data),
    },
    profile: {
      createApi: (_appId) => {
        return {
          getActiveProfile: () => {
            const id = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:activeProfileId') : null;
            if (!id) return null;
            const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:profiles') : null;
            if (!raw) return { id, name: 'Default' };
            try {
              const profiles = JSON.parse(raw);
              return profiles.find((p: any) => p.id === id) || { id, name: 'Default' };
            } catch {
              return { id, name: 'Default' };
            }
          },
          getActiveAccount: () => {
            const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:account') : null;
            if (!raw) return null;
            try {
              return JSON.parse(raw);
            } catch {
              return null;
            }
          },
          getProfiles: () => {
            const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:profiles') : null;
            if (!raw) return [];
            try {
              return JSON.parse(raw);
            } catch {
              return [];
            }
          },
          getEnabledApps: () => {
            const raw = typeof localStorage !== 'undefined' ? localStorage.getItem('yada:account:account') : null;
            if (!raw) return [];
            try {
              const acc = JSON.parse(raw);
              return acc.enabledApps || [];
            } catch {
              return [];
            }
          },
          hasHouseholdProfile: async () => hasHouseholdProfile(),
          getHouseholdProfile: async () => getHouseholdProfile(),
          setActiveProfile: async (id: string) => setActiveProfileId(id),
          onChange: (cb: () => void) => onProfileChange(cb),
        };
      },
    },
  });

  llmManager.setAppContextResolver(appId => collectPassiveAppContext(runtime, appId));

  (runtime as any).notificationManager = notificationManager;
  (runtime as any).llmManager = llmManager;

  if (typeof window !== 'undefined') {
    const onLocaleChange = () => runtime.notifyLocaleChange();
    window.addEventListener(COUNTRY_CHANGE_EVENT, onLocaleChange);
    window.addEventListener(LOCALE_CHANGE_EVENT, onLocaleChange);
  }

  return runtime as ShellRuntime;
}
